import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  CAMPAIGN_AUDIENCES,
  CAMPAIGN_CHANNELS,
  CAMPAIGN_STATUS,
  metaFor,
  optionsFrom,
  type Campaign,
} from "@/lib/adminContent";
import {
  DataTable,
  DetailList,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { formatDate } from "@/pages/admin/format";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_CAMPAIGN = {
  name: "",
  channel: "email",
  subject: "",
  audience: "All contacts",
  status: "draft",
  scheduled_at: "",
  sent_count: 0,
  open_rate: 0,
  click_rate: 0,
};

export default function AdminCampaigns() {
  const [rows, setRows] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [channelFilter, setChannelFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_CAMPAIGN });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Campaign>("campaigns", "created_at"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load campaigns.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Campaign>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("campaigns", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createCampaign = async () => {
    if (!draft.name.trim()) {
      setError("A campaign name is required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Campaign>("campaigns", {
        ...draft,
        name: draft.name.trim(),
        scheduled_at: draft.scheduled_at || null,
      });
      setRows((list) => [created, ...list]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_CAMPAIGN });
      notify(`Campaign "${created.name}" created.`);
    } catch {
      setError("We couldn't create that campaign. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number, name: string) => {
    try {
      await deleteRow("campaigns", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify(`Campaign "${name}" deleted.`);
    } catch {
      setError("We couldn't delete that campaign. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (channelFilter !== "all" && row.channel !== channelFilter) return false;
      if (!query) return true;
      return [row.name, row.subject || "", row.audience || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, channelFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;
  const sentCampaigns = rows.filter((row) => row.status === "sent");
  const avgOpen = sentCampaigns.length
    ? (sentCampaigns.reduce((sum, row) => sum + Number(row.open_rate || 0), 0) / sentCampaigns.length).toFixed(1)
    : "0.0";
  const totalReach = rows.reduce((sum, row) => sum + Number(row.sent_count || 0), 0);

  const columns: Column<Campaign>[] = [
    {
      key: "name",
      header: "Campaign",
      primary: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 shrink-0 flex items-center justify-center">
            <i className="ri-megaphone-line"></i>
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
            <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.subject || "No subject"}</p>
          </div>
        </div>
      ),
    },
    { key: "channel", header: "Channel", render: (row) => <StatusPill {...metaFor(CAMPAIGN_CHANNELS, row.channel)} withDot={false} /> },
    { key: "audience", header: "Audience", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.audience || "—"}</span> },
    { key: "reach", header: "Reach", render: (row) => <span className="text-sm text-foreground-700">{row.sent_count.toLocaleString("en-IN")}</span> },
    { key: "open", header: "Open rate", render: (row) => <span className="text-sm text-foreground-700">{row.status === "sent" ? `${row.open_rate}%` : "—"}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(CAMPAIGN_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Marketing"
        title="Campaigns"
        subtitle="Email, SMS and WhatsApp campaigns to past visitors, subscribers and partner audiences."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New Campaign
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total campaigns", value: rows.length, icon: "ri-megaphone-line", tone: "primary" },
          { label: "Sent", value: sentCampaigns.length, icon: "ri-send-plane-line", tone: "primary" },
          { label: "Scheduled", value: rows.filter((row) => row.status === "scheduled").length, icon: "ri-time-line", tone: "secondary" },
          { label: "Avg. open rate", value: `${avgOpen}%`, icon: "ri-mail-open-line", tone: "accent", hint: `${totalReach.toLocaleString("en-IN")} total reach` },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search by name, subject, audience…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(CAMPAIGN_STATUS)]} />
          <Select label="Channel" value={channelFilter} onChange={setChannelFilter} options={[{ value: "all", label: "All channels" }, ...optionsFrom(CAMPAIGN_CHANNELS)]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} campaigns</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading campaigns…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-megaphone-line" title={rows.length === 0 ? "No campaigns yet" : "No campaigns match"} message="Create a campaign to reach your audience." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title={selected?.name || "Campaign"}
        subtitle="Campaign detail"
        footer={
          selected ? (
            <div className="flex flex-wrap gap-2">
              {selected.status === "draft" ? (
                <button type="button" onClick={() => update(selected.id, { status: "scheduled" }, "Campaign scheduled.")} className={btnPrimary}>
                  <i className="ri-calendar-check-line"></i> Schedule
                </button>
              ) : null}
              {selected.status !== "sent" ? (
                <button type="button" onClick={() => update(selected.id, { status: "sent" }, "Campaign sent.")} className={btnPrimary}>
                  <i className="ri-send-plane-line"></i> Mark as sent
                </button>
              ) : null}
              <button type="button" onClick={() => remove(selected.id, selected.name)} className={btnGhost}>
                <i className="ri-delete-bin-line"></i> Delete
              </button>
            </div>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(CAMPAIGN_STATUS, selected.status)} />
              <StatusPill {...metaFor(CAMPAIGN_CHANNELS, selected.channel)} withDot={false} />
            </div>

            <DetailList
              items={[
                { icon: "ri-mail-line", label: "Subject", value: selected.subject || "—" },
                { icon: "ri-group-line", label: "Audience", value: selected.audience || "—" },
                { icon: "ri-send-plane-line", label: "Reach", value: selected.sent_count.toLocaleString("en-IN") },
                { icon: "ri-mail-open-line", label: "Open rate", value: selected.status === "sent" ? `${selected.open_rate}%` : "—" },
                { icon: "ri-cursor-line", label: "Click rate", value: selected.status === "sent" ? `${selected.click_rate}%` : "—" },
                { icon: "ri-calendar-line", label: "Scheduled", value: formatDate(selected.scheduled_at) },
              ]}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Channel">
                <select value={selected.channel} onChange={(e) => update(selected.id, { channel: e.target.value }, "Channel updated.")} className={fieldInput}>
                  {optionsFrom(CAMPAIGN_CHANNELS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </Field>
              <Field label="Status">
                <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value }, "Status updated.")} className={fieldInput}>
                  {optionsFrom(CAMPAIGN_STATUS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
                </select>
              </Field>
            </div>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Create a campaign"
        subtitle="New campaign"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={createCampaign} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Create campaign
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Campaign name">
            <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Harvest Season Newsletter" className={textInput} />
          </TextField>
          <TextField label="Channel">
            <select value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value })} className={fieldInput}>
              {optionsFrom(CAMPAIGN_CHANNELS).map((option) => (<option key={option.value} value={option.value}>{option.label}</option>))}
            </select>
          </TextField>
          <div className="sm:col-span-2">
            <TextField label="Subject / message">
              <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className={textInput} />
            </TextField>
          </div>
          <TextField label="Audience">
            <select value={draft.audience} onChange={(e) => setDraft({ ...draft, audience: e.target.value })} className={fieldInput}>
              {CAMPAIGN_AUDIENCES.map((audience) => (<option key={audience} value={audience}>{audience}</option>))}
            </select>
          </TextField>
          <TextField label="Scheduled for">
            <input type="datetime-local" value={draft.scheduled_at} onChange={(e) => setDraft({ ...draft, scheduled_at: e.target.value })} className={textInput} />
          </TextField>
        </div>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}